'use client'

import { useState, useEffect } from 'react'
import { Button } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@planner/ui'
import { useServices } from '../hooks/useServices'
import { useServiceCategories } from '../hooks/useServiceCategories'
import type { Service, ServicePricingType } from '@planner/types'

interface ServiceFormProps {
  service?: Service | null
  onClose: () => void
}

export function ServiceForm({ service, onClose }: ServiceFormProps) {
  const { createService, updateService } = useServices(true)
  const { categories } = useServiceCategories()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    service_category_id: '',
    pricing_type: 'per_unit' as ServicePricingType,
    price: '',
    unit_label: 'szt.',
    is_active: true,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name,
        description: service.description || '',
        service_category_id: service.service_category_id || '',
        pricing_type: service.pricing_type,
        price: service.price.toString(),
        unit_label: service.unit_label,
        is_active: service.is_active,
      })
    }
  }, [service])

  const getDefaultUnitLabel = (pricingType: ServicePricingType) => {
    switch (pricingType) {
      case 'per_person':
        return 'os.'
      case 'per_unit':
        return 'szt.'
      case 'per_hour':
        return 'godz.'
      case 'fixed':
        return 'szt.'
      default:
        return 'szt.'
    }
  }

  const handlePricingTypeChange = (pricingType: ServicePricingType) => {
    setFormData({
      ...formData,
      pricing_type: pricingType,
      unit_label: getDefaultUnitLabel(pricingType),
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const serviceData = {
        name: formData.name,
        description: formData.description || null,
        service_category_id: formData.service_category_id || null,
        pricing_type: formData.pricing_type,
        price: parseFloat(formData.price),
        unit_label: formData.unit_label,
        is_active: formData.is_active,
      }

      if (service) {
        const result = await updateService(service.id, serviceData)
        if (result.error) {
          setError(result.error)
          setLoading(false)
          return
        }
      } else {
        const result = await createService(serviceData)
        if (result.error) {
          setError(result.error)
          setLoading(false)
          return
        }
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{service ? 'Edytuj usługę' : 'Nowa usługa'}</CardTitle>
        <CardDescription>
          {service ? 'Zaktualizuj informacje o usłudze' : 'Dodaj nową usługę dodatkową'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="name">Nazwa usługi *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Opis</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="service_category_id">Kategoria</Label>
            <select
              id="service_category_id"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.service_category_id}
              onChange={(e) => setFormData({ ...formData, service_category_id: e.target.value })}
            >
              <option value="">Bez kategorii</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="pricing_type">Typ wyceny *</Label>
            <select
              id="pricing_type"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.pricing_type}
              onChange={(e) => handlePricingTypeChange(e.target.value as ServicePricingType)}
              required
            >
              <option value="per_person">Za osobę</option>
              <option value="per_unit">Za sztukę</option>
              <option value="per_hour">Za godzinę</option>
              <option value="fixed">Stała cena</option>
            </select>
          </div>

          <div>
            <Label htmlFor="price">Cena *</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="unit_label">Etykieta jednostki</Label>
            <Input
              id="unit_label"
              value={formData.unit_label}
              onChange={(e) => setFormData({ ...formData, unit_label: e.target.value })}
              placeholder="np. szt., os., godz."
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="is_active" className="cursor-pointer">
              Usługa aktywna (widoczna dla klientów)
            </Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Anuluj
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Zapisywanie...' : service ? 'Zaktualizuj' : 'Utwórz'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}


